import firebase from "firebase/app";
import { useSelector, useDispatch } from "react-redux";
import { useContext, useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import useSegment from "../../components/utils/useSegment";
import { AppState } from "../root-reducer";
import { CartState } from "./types";
import { useNotifications } from "../../components/Notifications";
import { userStore } from "../../components/utils/UserContext";
import { createUserCodeRedemptionMutation } from "../../graphql/UserCodeRedemption/createUserCodeRedemption";
import {
  cartAddItemAction,
  cartRemoveItemAction,
  cartAbortPaymentAction,
  cartSuccessPaymentAction,
  cartStartPaymentAction,
  cartUpdatePricingAction,
  cartFailPaymentAction,
} from "./actions";
import { CourseType, CartItemType, CartItemsType } from "../../types";
import { usePromoCode } from "../../components/CheckoutView";
import usePricing from "../pricing/usePricing";
import ROUTES from "../../components/utils/routes";

const useCart = () => {
  const {
    cartItems,
    purchasedItems,
    paymentLoading,
    paymentSuccess,
    paymentError,
  } = useSelector<AppState, CartState>((state) => state.shoppingCart);

  const { track } = useSegment();
  const { addNotification } = useNotifications();
  const {
    pricing,
    actions: { getPriceId, getPrice, getPriceDiscounted },
  } = usePricing();
  const { validatePromoCode } = usePromoCode();
  const { user } = useContext(userStore);

  const [createUserCodeRedemption] = useMutation(
    createUserCodeRedemptionMutation
  );
  const [discountCode, setDiscountCode] = useState("");
  const [discountCodeError, setDiscountCodeError] = useState("");
  const [discount, setDiscount] = useState(0);
  const [validatedDiscountCode, setValidationDiscountCode] = useState(false);
  const [total, setTotal] = useState(0);

  const dispatch = useDispatch();
  const history = useHistory();
  const buyCartItems = firebase.functions().httpsCallable("buyCartItems");

  const idInCart = (id: string) => {
    if (!cartItems) {
      return false;
    }

    return cartItems.some((cartItem) => cartItem.itemData._id === id);
  };

  const updateCartItemPricing = (id: string, priceId: string) => {
    dispatch(cartUpdatePricingAction({ id, priceId }));
  };

  const addToCart = (cartItem: CartItemType, from?: string) => {
    dispatch(cartAddItemAction(cartItem));

    const course = cartItem.itemData as CourseType;
    addNotification({ type: "success", message: "Added Course To Cart" });
    track("Course Added To Cart", {
      title: course.title,
      id: course._id,
      price: getPrice(cartItem.type),
      discounted_price: getPriceDiscounted(cartItem.type),
      from,
    });
  };

  const removeFromCart = (id: string, type: CartItemsType) => {
    dispatch(cartRemoveItemAction({ id, type }));
    addNotification({ type: "success", message: "Removed From Cart" });
  };

  const abortPayment = () => {
    dispatch(cartAbortPaymentAction());
  };

  const handlePromoCodeValidation = async () => {
    try {
      const responseCode = await validatePromoCode(discountCode);
      setValidationDiscountCode(true);
      setDiscountCodeError("");
      setDiscountCode(responseCode!.stripeID);
      setDiscount(calculateDiscount(responseCode!.type, responseCode!.amount));
    } catch (err) {
      setDiscountCodeError(err.message);
    }
  };

  // Calculate the current cart's discounted amount based on the discount type and amount
  const calculateDiscount = (
    type: "percentage" | "amount",
    amount: number,
    providedTotal?: number
  ) => {
    if (type === "percentage") {
      // if no total is provided, fall back to `total` in state
      const newTotal = providedTotal || total;
      return newTotal * (amount / 100);
    }

    return amount;
  };

  const calcCartTotal = () => {
    const cartTotal = cartItems.reduce(
      (total: number, cartItem: CartItemType) => {
        return total + getPriceDiscounted(cartItem.priceId || cartItem.type);
      },
      0
    );
    setTotal(cartTotal - discount);
  };

  const processPayment = async () => {
    dispatch(cartStartPaymentAction());
    const itemsWithStripeIds = cartItems.map((item) => ({
      ...item,
      priceId: item.priceId || getPriceId(item.type),
      price: getPriceDiscounted(item.type),
    }));

    try {
      await buyCartItems({
        purchasedItems: itemsWithStripeIds,
        discountCode,
      });

      if (discountCode) {
        await createUserCodeRedemption({
          variables: {
            code: discountCode,
            user: user?.id,
          },
        });
      }

      handleRedirect();

      return { success: true };
    } catch (err) {
      dispatch(cartFailPaymentAction(err.message));
    }
  };

  const handleRedirect = () => {
    if (!cartItems) {
      return;
    }

    dispatch(cartSuccessPaymentAction());
    history.push(ROUTES.receipt, { items: cartItems });
  };

  useEffect(() => {
    calcCartTotal();
  }, [cartItems, discount, pricing]);

  return {
    cartItems,
    purchasedItems,
    discount,
    validatedDiscountCode,
    discountCode,
    discountCodeError,
    paymentLoading,
    paymentSuccess,
    paymentError,
    total,
    actions: {
      idInCart,
      addToCart,
      removeFromCart,
      processPayment,
      abortPayment,
      updateCartItemPricing,
      calculateDiscount,
      setDiscount,
      setDiscountCode,
      handlePromoCodeValidation,
    },
  };
};

export default useCart;
